import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fireworks } from 'fireworks-js'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Spinner } from '../App/Components'

import { AdminHeader } from './components/AdminHeader'
import { AdminSquare } from './components/AdminSquare'
import { UserMessage } from './components/UserMessage'
import { Container, PageWrapper, Title, FireWorkOverlay } from './style'
import {
  transformStatData,
  useGetEstablishmentStatsPermissionsQuery,
  useGetEstablishmentStatsQuery,
  useGetPersonInfoQuery,
} from './util/useEstablishmentStatsQuery'

const ContainerWrapper = styled.div`
  position: relative;
  justify-content: center;
`
const SpinnerContainer = styled.div`
  position: fixed;
  top: 0;
  right: -1%;
  height: 100%;
  width: 102%;
  z-index: 9;
  background-color: rgba(153, 147, 135, 0.3);
`

const AdminTitle = styled(Title)`
  margin: 40px 0 20px 0;
`

const AdminSubTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 400;
  text-align: center;
  color: white;
  margin-bottom: 20px;
`

export const EstablishmentStats = () => {
  const [showLoaderOverlay, setShowLoaderOverlay] = useState(false)
  const [node, setNode] = useState<HTMLDivElement | null>(null)

  const { t } = useTranslation()

  const { data, status } = useGetEstablishmentStatsQuery()
  const establishmentSharedPermissions =
    useGetEstablishmentStatsPermissionsQuery()

  const personInfo = useGetPersonInfoQuery()

  const permissionsState = establishmentSharedPermissions.status

  const refCallback = (element: HTMLDivElement) => setNode(element)

  useEffect(() => {
    if (node) {
      const fireworks = new Fireworks(node)
      fireworks.start()

      const timeoutId = setTimeout(() => {
        fireworks.stop(true)
      }, 12000)

      return () => clearTimeout(timeoutId)
    }
  }, [node])

  if (status === 'pending' || permissionsState === 'pending') return <Spinner />

  if (status === 'error' || permissionsState === 'error')
    return <p>{t('Something went wrong')}</p>

  const statsEntries = transformStatData(
    data._resource,
    establishmentSharedPermissions.data,
  )

  const statsDate = data._meta.query_filters.starts_at.substring(0, 4)

  const isPartner = personInfo.data?.data.type === 'partners'
  const isManager = personInfo.data?.data.attributes.is_manager
  const canEdit = isPartner || isManager

  const NonAdminMessages = [
    'Only managers can share these stats. Ask your manager to share on social media.',
  ]

  const AdminMessages = [
    'Would you like to publish your stats?',
    'Check the boxes that you would like to share publicly.',
    'CAREFUL! Verify first through the preview if you selected the right stats before sharing the link.',
  ]

  return (
    <PageWrapper>
      <FireWorkOverlay ref={refCallback} />
      <UserMessage messages={canEdit ? AdminMessages : NonAdminMessages} />
      {canEdit ? <AdminHeader /> : null}
      <AdminTitle>{`${statsDate} ${t('with Optios')}`}</AdminTitle>
      <AdminSubTitle>{`🎊 ${t(
        'Congratulations on your hard work in',
      )} ${statsDate}! 🎊`}</AdminSubTitle>
      <ContainerWrapper>
        {showLoaderOverlay ? (
          <SpinnerContainer>
            <Spinner containerMargin="50% auto" />
          </SpinnerContainer>
        ) : null}
        <div>
          <Container>
            {statsEntries.map(stats => (
              <AdminSquare
                canPersonEdit={!!isPartner || !!isManager}
                key={stats.key}
                data={stats}
                refData={statsEntries}
                setShowLoaderOverlay={setShowLoaderOverlay}
              />
            ))}
          </Container>
        </div>
      </ContainerWrapper>
    </PageWrapper>
  )
}
